/**
 * useApiEndpointForm
 * 
 * Hook que gerencia o estado do formulário de API endpoint.
 * Princípios: Single Responsibility, Separation of Concerns
 */

import { useState, useEffect, useCallback } from 'react';
import { ApiEndpoint } from '../../../../types';
import { notify } from '../../../../utils/toast';

export type AuthType = 'bearer' | 'basic';
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface ApiEndpointFormData {
    name: string;
    slug: string;
    description: string;
    authType: AuthType;
    authToken: string;
    basicUsername: string;
    basicPassword: string;
    httpMethod: HttpMethod;
    allowedActions: string[];
    isActive: boolean;
}

interface UseApiEndpointFormProps {
    isOpen: boolean;
    endpointToEdit?: ApiEndpoint;
    generateToken: () => string;
    generateSlug: (name: string) => string;
}

export const useApiEndpointForm = ({
    isOpen,
    endpointToEdit,
    generateToken,
    generateSlug
}: UseApiEndpointFormProps) => {
    const [form, setForm] = useState<ApiEndpointFormData>({
        name: '',
        slug: '',
        description: '',
        authType: 'bearer',
        authToken: '',
        basicUsername: '',
        basicPassword: '',
        httpMethod: 'POST',
        allowedActions: [],
        isActive: false
    });
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

    // Reset form quando modal abre
    useEffect(() => {
        if (!isOpen) return;

        if (endpointToEdit) {
            setForm({
                name: endpointToEdit.name,
                slug: endpointToEdit.slug,
                description: endpointToEdit.description || '',
                authType:
                    endpointToEdit.authType === 'basic' ? 'basic' : 'bearer',
                authToken: endpointToEdit.authToken || generateToken(),
                basicUsername: endpointToEdit.basicUsername || '',
                basicPassword: endpointToEdit.basicPassword || '',
                httpMethod: endpointToEdit.httpMethods[0] || 'POST',
                allowedActions: endpointToEdit.allowedActions,
                isActive: endpointToEdit.isActive
            });
            setSlugManuallyEdited(true);
        } else {
            setForm({
                name: '',
                slug: '',
                description: '',
                authType: 'bearer',
                authToken: generateToken(),
                basicUsername: '',
                basicPassword: '',
                httpMethod: 'POST',
                allowedActions: [],
                isActive: true
            });
            setSlugManuallyEdited(false);
        }
    }, [isOpen, endpointToEdit, generateToken]);

    // Auto-gerar slug
    useEffect(() => {
        if (!slugManuallyEdited && form.name) {
            setForm(prev => ({ ...prev, slug: generateSlug(form.name) }));
        }
    }, [form.name, slugManuallyEdited, generateSlug]);

    const updateField = useCallback(<K extends keyof ApiEndpointFormData>(
        field: K,
        value: ApiEndpointFormData[K]
    ) => {
        setForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const updateSlug = useCallback((value: string) => {
        setForm(prev => ({ ...prev, slug: value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }));
        setSlugManuallyEdited(true);
    }, []);

    const setHttpMethod = useCallback((method: HttpMethod) => {
        setForm(prev => ({ ...prev, httpMethod: method }));
    }, []);

    const toggleAction = useCallback((actionId: string) => {
        setForm(prev => ({
            ...prev,
            allowedActions: prev.allowedActions.includes(actionId)
                ? prev.allowedActions.filter(a => a !== actionId)
                : [...prev.allowedActions, actionId]
        }));
    }, []);

    const regenerateToken = useCallback(() => {
        setForm(prev => ({ ...prev, authToken: generateToken() }));
        notify.success('Novo token gerado!');
    }, [generateToken]);

    const getFormData = useCallback((): Omit<ApiEndpoint, 'id' | 'createdAt' | 'updatedAt' | 'requestCount' | 'lastUsedAt'> => ({
        name: form.name,
        slug: form.slug,
        description: form.description || undefined,
        authType: form.authType,
        authToken: form.authType === 'bearer' ? form.authToken : undefined,
        basicUsername: form.authType === 'basic' ? form.basicUsername : undefined,
        basicPassword: form.authType === 'basic' ? form.basicPassword : undefined,
        httpMethods: [form.httpMethod],
        allowedActions: form.allowedActions,
        isActive: form.isActive
    }), [form]);

    const validate = useCallback((): boolean => {
        if (!form.slug.trim()) {
            notify.error('O slug é obrigatório');
            return false;
        }
        return true;
    }, [form.slug]);

    return {
        form,
        updateField,
        updateSlug,
        setHttpMethod,
        toggleAction,
        regenerateToken,
        getFormData,
        validate
    };
};
